import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LoadingButton from '@mui/lab/LoadingButton';
import { MAX_TITLE_LENGTH, MIN_TITLE_LENGTH } from '@opengov/stories-shared/constants';
import React, { useEffect, useMemo, useState } from 'react';

import { IOnlinePublicationMessages, IStoriesFolder } from '../../../../../../types';
import { useSetupOnlinePublicationUi } from '../../context/SetupOnlinePublicationUi.context';

import { styles } from './AddMenuModal.styles';

export type IAddMenuModalProps = {
  messages: IOnlinePublicationMessages;
  menuItems: IStoriesFolder[];
  publicationId: string;
  userId: number;
  insertPublicationNavigationMenu(props: {
    publicationId: string;
    title: string;
    userId: number;
    order: number;
  }): Promise<unknown>;
  onClose(): void;
};

export const AddMenuModal: React.FC<IAddMenuModalProps> = (props) => {
  const { menuItems, messages, publicationId, userId, insertPublicationNavigationMenu, onClose } = props;
  const addMenuDescription = messages.addMenuDescription as string | undefined;

  const [menuName, setMenuName] = useState('');
  const [isAddingMenu, setIsAddingMenu] = useState(false);
  const [hasSameName, setHasSameName] = useState(false);
  const trimmedMenuName = useMemo(() => menuName.trim(), [menuName]);
  let errorMessage;

  useEffect(() => {
    if (menuItems) {
      const hasMatch = !!menuItems.find((menu) => menu.displayName.toLowerCase() === trimmedMenuName.toLowerCase());
      setHasSameName(hasMatch);
    }
  }, [trimmedMenuName, menuItems]);

  if (hasSameName) {
    errorMessage = messages.noSameName;
  }

  const { dispatch: uiDispatch } = useSetupOnlinePublicationUi();

  const addButtonAction = () => {
    const lastOrder = menuItems[menuItems.length - 1]?.order;
    const order = lastOrder !== undefined ? lastOrder + 1 : 1;
    setIsAddingMenu(true);
    void insertPublicationNavigationMenu({ publicationId, userId, title: trimmedMenuName, order }).then(() => {
      setIsAddingMenu(false);
      uiDispatch({ type: 'SET_UPDATE_PUBLICATION_PREVIEW', needsRefresh: true });
      onClose();
    });
  };

  const isMenuItemsLimitReached = menuItems?.length >= 7;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth data-test="add-menu-modal" aria-labelledby="add-menu-dialog-title">
      <DialogTitle id="add-menu-dialog-title" sx={styles.dialogTitle}>
        {messages.addMenu}
        <IconButton aria-label="Close" onClick={onClose} sx={styles.closeButton}>
          <CloseIcon aria-hidden={true} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {addMenuDescription && (
          <Typography variant="body2" color="text.secondary">
            {addMenuDescription}
          </Typography>
        )}
        {isMenuItemsLimitReached && (
          <Box sx={styles.menuMaxLimitMessageContainer} data-test="menu-max-limit-message">
            <Box>
              <WarningAmberIcon aria-hidden={true} sx={styles.warningIcon} />
            </Box>
            <Typography variant="caption">{messages.maxMenuLimit}</Typography>
          </Box>
        )}
        <Box sx={styles.labelContainer}>
          <Typography id="add-menu-name-label" variant="body2" sx={styles.formLabel}>
            {messages.menuName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {`${trimmedMenuName.length}/${MAX_TITLE_LENGTH}`}
          </Typography>
        </Box>
        <TextField
          required
          fullWidth
          value={menuName}
          onChange={(e) => setMenuName(e.target.value)}
          placeholder={messages.enterName}
          inputProps={{
            maxLength: MAX_TITLE_LENGTH,
            'aria-labelledby': 'add-menu-name-label',
            'aria-describedby': hasSameName ? 'add-menu-name-error' : undefined,
            'data-test': 'add-menu-modal-name-input',
            className: 'no-format'
          }}
        />
        {errorMessage && (
          <Typography id="add-menu-name-error" variant="caption" color="error">
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button variant="outlined" color="secondary" size="small" data-test="add-menu-modal-cancel-button" onClick={onClose}>
          {messages.cancel}
        </Button>
        <LoadingButton
          loading={isAddingMenu}
          variant="contained"
          size="small"
          data-test="add-menu-modal-add-button"
          disabled={
            trimmedMenuName.length < MIN_TITLE_LENGTH ||
            trimmedMenuName.length > MAX_TITLE_LENGTH ||
            hasSameName ||
            isMenuItemsLimitReached
          }
          onClick={addButtonAction}
        >
          {messages.add}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
